import { HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { catchError, Observable, throwError, of } from 'rxjs';
import { UserService } from '../user/user.service';
import { PermissionErrorHandlerService } from '../error/permission-error-handler.service';

/**
 * Auth Interceptor
 * 
 * Handles authentication and authorization for HTTP requests:
 * - Adds Authorization header with JWT token
 * - Validates user session consistency
 * - Handles 401 (Unauthorized) errors → logout and redirect
 * - Handles 403 (Forbidden) errors → user-friendly permission denied message
 * - Skips auth for assets and third-party APIs
 * 
 * @param req - The outgoing HTTP request
 * @param next - The next handler in the interceptor chain
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const _userService = inject(UserService);
    const errorHandler = inject(PermissionErrorHandlerService);

    // Clone the request object
    let newReq = req.clone();

    // Skip adding Authorization header for:
    // - Local asset requests
    // - Third-party external API requests (IOC, geolocation, etc.)
    // - Proxied third-party APIs (like /blackbox)
    const isAssetRequest = req.url.startsWith('/assets/') || req.url.startsWith('assets/');
    const isProxiedThirdParty = req.url.startsWith('/blackbox');
    const isExternalThirdParty = (req.url.startsWith('http://') || req.url.startsWith('https://')) && 
                                 !req.url.includes('honeytokens-api') && 
                                 !req.url.includes('abacus-consulting.net');
    const isThirdPartyAPI = isProxiedThirdParty || isExternalThirdParty;

    // Request
    //
    // If the access token didn't expire, add the Authorization header.
    // We won't add the Authorization header if the access token expired.
    // This will force the server to return a "401 Unauthorized" response
    // for the protected API routes which our response interceptor will
    // catch and delete the access token from the local storage while logging
    // the user out from the app.
    if (!isAssetRequest && !isThirdPartyAPI && authService.accessToken && _userService.currentUser && localStorage.getItem('user'))// && !AuthUtils.isTokenExpired(authService.accessToken) )
    {
        const userId = JSON.parse(localStorage.getItem('user')).id
        const role = JSON.parse(localStorage.getItem('user')).role
        if (!(_userService.currentUser.id === userId && role === _userService.currentUser.role)) {
            // Sign out
            authService.signOut();
        }
        if (!req.headers.has('Authorization')) {
            newReq = req.clone({
                headers: req.headers.set('Authorization', 'Bearer ' + authService.accessToken),
            });
        }
    }

    // Response
    return next(newReq).pipe(
        catchError((error) => {
            // Only handle errors from our own API, not third-party APIs
            const isProxiedThirdParty = req.url.startsWith('/blackbox');
            const isExternalThirdParty = (req.url.startsWith('http://') || req.url.startsWith('https://')) && 
                                         !req.url.includes('honeytokens-api') && 
                                         !req.url.includes('abacus-consulting.net');
            const isThirdPartyAPI = isProxiedThirdParty || isExternalThirdParty;
            
            if (!isThirdPartyAPI) {
                // Handle 401 Unauthorized - Session expired
                if (error.status === 401) {
                    // Clear authentication state and redirect to login
                    authService.signOut();
                    errorHandler.handle401Error();
                    // Force redirect to sign-in page
                    window.location.href = '/sign-in';
                    return throwError(() => error);
                }
                
                // Handle 403 Forbidden
                if (error.status === 403) {
                    // Special case: Statistics endpoint returns 403 when user has no projects
                    // This is not a real error, just an informational message with valid empty stats
                    // Check both original and new request URLs
                    const isStatisticsEndpoint = req.url.includes('/api/statistics') || 
                                                newReq.url.includes('/api/statistics') ||
                                                req.url.endsWith('/statistics') ||
                                                newReq.url.endsWith('/statistics');
                    
                    if (isStatisticsEndpoint) {
                        const errorMessage = error?.error?.error || '';
                        const hasValidStats = error?.error?.global_statistics !== undefined;
                        
                        if (errorMessage.includes('No tiene acceso a ningún proyecto') && hasValidStats) {
                            // Transform 403 into successful 200 response with the provided data
                            // Suppress the error to prevent console logging
                            return of(new HttpResponse({
                                status: 200,
                                statusText: 'OK',
                                body: error.error,
                                headers: error.headers,
                                url: error.url || undefined
                            }));
                        }
                    }
                    
                    // Generic 403 handling - Permission denied
                    // Extract context from request URL for better error messages
                    let action = '';
                    if (req.method === 'GET') action = 'access';
                    else if (req.method === 'POST') action = 'create';
                    else if (req.method === 'PUT' || req.method === 'PATCH') action = 'update';
                    else if (req.method === 'DELETE') action = 'delete';
                    
                    // Show user-friendly error (doesn't navigate, just informs)
                    errorHandler.handle403Error(error, action ? `${action} this resource` : undefined);
                }
            }
            
            return throwError(() => error);
        }),
    );
};
